// @flow

import React, {useEffect} from "react";
import Loading from "../layout/Loading";
import type BrandInstance from "../../models/Brand";
import {withTranslation} from "react-i18next";
import type {translate} from "../../i18next";
import "./BrandInstance.scss";
import Placeholder2x1 from "../../assets/ph2x1.png";
import type {GarageDealership} from "../../models/GarageDealership";
import {emitInternalEvent} from "../../helper/tracker/internalEvents";

export type BrandInstanceProps = {
    shouldRender: boolean,
    brandInstances?: GarageDealership[],
    autoselectedInstance: GarageDealership,
    autoselectSource: ?string,
    disableConfig: () => void,
    selectBrand: (brand: GarageDealership, selectionSource?: string) => void,
    // embed override (data-brandselect-override-headline): null = translated default,
    // "" = no headline at all, anything else is rendered 1:1 (#56151)
    overrideHeadline?: ?string,
    t: translate,
}

const BrandInstancePage = ({
                               autoselectedInstance,
                               autoselectSource,
                               brandInstances,
                               overrideHeadline,
                               selectBrand,
                               shouldRender,
                               t
                           }: BrandInstanceProps) => {

    useEffect(() => {
        if (autoselectedInstance) {
            selectBrand(autoselectedInstance, autoselectSource);
        }
    }, [autoselectedInstance, autoselectSource, selectBrand, brandInstances]);

    useEffect(() => {
        if (!autoselectedInstance && brandInstances && brandInstances.length > 0) {
            emitInternalEvent("brand_select_view", {
                shown_brands: brandInstances.map(brand => brand.manufacturerName),
            });
        }
    }, [autoselectedInstance, brandInstances]);

    const renderBrands = (brands?: BrandInstance[]) => {
        if (brands === null) return;
        return brands.map((brand, index) => {
            const brandLogo = {
                backgroundImage: `url(${brand.manufacturerLogoUrl})`
            };
            const brandSize = {
                flex: brands.length <= 4
                    ? "0 0 45%"
                    : brands.length <= 6
                        ? "0 0 30%"
                        : "unset"
            };
            return (
                <button key={index} className="hpm-brand"
                        onClick={() => selectBrand(brand)}
                        onKeyDown={e => {
                            if (e.key === "Enter" || e.key === " ") {
                                selectBrand(brand);
                            }
                        }}
                        aria-label={brand.manufacturerName}
                        tabIndex={0}
                        style={brandSize}>
                    <img src={Placeholder2x1} role={"presentation"} alt=""/>
                    <div className="hpm-brandLogo" style={brandLogo}/>
                </button>
            );
        });
    };

    if (brandInstances === null) {
        return <Loading/>;
    }

    const headline = overrideHeadline ?? t("brandSelector.title");

    return shouldRender
        ? <div className="hpm-brand-instance">
            {brandInstances.length
                ? <>
                    {headline !== "" &&
                        <div className="hpm-brand-title">{headline}</div>}
                    <div className="hpm-brands">
                        {renderBrands(brandInstances)}
                    </div>
                </>
                : <div className="hpm-brand-title">{t("brandSelector.noBrands")}</div>
            }
        </div>
        : <Loading/>;
};

export default withTranslation()(BrandInstancePage);
